// Modified by AI on 06/24/2026. Edit #1.
import {
    chain, chunk, difference, find, get, isNil,
} from 'lodash';

import React, {
    useCallback, useState, useContext, useEffect, useMemo,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import PositionContext from './PositionContext';
import EmployerJobCandidatesHttpService from '../api/EmployerJobCandidatesHttpService';
import Logger from '../services/logger';
import SettingsContext from './SettingsContext';
import CandidatesStoreContext from './CandidatesStoreContext';
import { FIVE_MINUTES_MS, QUERY_KEYS } from '../constants/queryKeys';

const ExplainInDetailsLevel = {
    minimal: 0,
    minimalWithSkills: 1,
    full: 2,
    scoringComputationDetails: 3,
};

const CANDIDATE_LIST_EXPLAINS_CHUNK_SIZE = 20;

const ExplainContext = React.createContext({});

export const AppWithExplains = ({ children }) => {
    // Modified by AI on 06/24/2026. Edit #2.
    const queryClient = useQueryClient();
    const { position } = useContext(PositionContext);

    const [explainsMap, setExplainsMap] = useState({});
    const [locationsMap, setLocationsMap] = useState({});
    const [areLocationsFetched, setLocationFetched] = useState(false);
    const [explainFetchingState, setExplainFetchingState] = useState({});
    const [allTalentsIds, setAllTalentIds] = useState([]);
    const [sampleExplainsMap, setSampleExplainsMap] = useState({});

    // Modified by AI on 06/30/2026. Edit #1.
    const { allCandidatesTalentIds } = useContext(CandidatesStoreContext);

    useEffect(() => {
        setAllTalentIds(allCandidatesTalentIds);
    }, [allCandidatesTalentIds]);

    const {
        systemConfiguration: {
            searchEngineFilterTotalTalentPoolThresholds: totalTalentPoolThresholds,
        },
    } = useContext(SettingsContext);

    useEffect(() => {
        if (allTalentsIds.length) {
            fetchLocationsFilter();
        }
    }, [allTalentsIds]);

    const wrapFunction = useCallback((functionToWrap, timeToWait) => {
        let talentsIdStack = [];
        let sampleTalentsIdStack = [];
        let timeoutId = null;

        return (talentId, isSample) => new Promise((resolve) => {
            if ((!isSample && !talentsIdStack.includes(talentId)) || (isSample && !sampleTalentsIdStack.includes(talentId))) {
                setExplainFetchingState((state) => ({ ...state, [talentId]: true }));
                if (isSample) {
                    sampleTalentsIdStack.push(talentId);
                } else {
                    talentsIdStack.push(talentId);
                }
            }

            if (!timeoutId) {
                timeoutId = setTimeout(async () => {
                    const talentIdsToFetch = [...talentsIdStack];
                    talentsIdStack = [];

                    const sampleTalentIdsToFetch = [...sampleTalentsIdStack];
                    sampleTalentsIdStack = [];

                    let result = [];

                    if (talentIdsToFetch?.length) {
                        result = result.concat(await functionToWrap(talentIdsToFetch));
                    }

                    if (sampleTalentIdsToFetch?.length) {
                        result = result.concat(await functionToWrap(sampleTalentIdsToFetch, isSample));
                    }

                    resolve(result);
                    timeoutId = null;
                }, timeToWait);
            }
        });
    }, []);

    const fetchLocationsFilter = useCallback(async () => {
        setLocationFetched(false);

        try {
            const filter = {
                totalTalentPoolThresholds,
                cascadingSearch: true,
                explainInDetails: ExplainInDetailsLevel.minimalWithSkills,
                ...position.desiredCandidateProfile,
            };

            const locationsFilter = (filter.selected || []).find(
                (selectedItem) => selectedItem.field === 'locations',
            ) || {};

            (locationsFilter.items || []).forEach((location) => {
                // eslint-disable-next-line no-param-reassign
                location.label = location.text;
            });

            const alreadyFetchedExplainsTalentIds = Object.keys(explainsMap);
            const talentIdsMissingExplains = difference(allTalentsIds, alreadyFetchedExplainsTalentIds);
            const chunkedTalentIds = chunk(talentIdsMissingExplains, CANDIDATE_LIST_EXPLAINS_CHUNK_SIZE);
            const promises = [];
            for (const oneChunk of chunkedTalentIds) {
                promises.push(fetchExplains(oneChunk));
            }

            await Promise.all(promises);
            setExplainsMap((currentState) => {
                const explains = chain(currentState).values().filter(({ id }) => allTalentsIds.includes(id))
                    .value();

                const newLocationsMap = explains.reduce((acc, explain) => {
                    const locationCriteria = (get(explain, 'scoring', []) || [])
                        .find((scoringItem) => scoringItem.filterName === 'locations');

                    const locationNames = get(locationCriteria, 'criteria', []);
                    const targetLocations = locationNames.filter((t) => t.score > 0);

                    for (const location of targetLocations) {
                        const locationName = get(location, 'label', get(location, 'text'));
                        if (locationName) {
                            const locationItem = find(locationsFilter?.items, (item) => item?.text === locationName);
                            const isExcluded = location?.exclude || locationItem?.additional?.exclude;
                            if (!acc) {
                                // eslint-disable-next-line no-param-reassign
                                acc = {};
                            }

                            if (!acc[locationName]) {
                                acc[locationName] = {
                                    count: 0,
                                    talentIds: {},
                                    ...(isExcluded && { isExcluded }),
                                };
                            }

                            if (!acc[locationName].talentIds[explain.id]) {
                                acc[locationName].count += 1;
                                acc[locationName].talentIds[explain.id] = explain.id;
                            }
                        }
                    }

                    return acc;
                }, {});

                setLocationsMap(newLocationsMap);
                setLocationFetched(true);
                return currentState;
            });
        } catch (error) {
            Logger.error(error);
        }
    }, [position, totalTalentPoolThresholds, allTalentsIds, explainsMap, setExplainsMap]);

    // Modified by AI on 06/24/2026. Edit #3.
    const fetchExplains = useCallback(async (talentIDs, isSamples) => {
        try {
            const positionId = get(position, '_id');

            if (!positionId) {
                throw new Error('No position data provided');
            }

            const talentIdsKey = chain(talentIDs).compact().sortBy().join(',')
                .value();

            const explains = await queryClient.fetchQuery({
                queryKey: QUERY_KEYS.explain.candidates(positionId, talentIdsKey, isSamples),
                queryFn: () => EmployerJobCandidatesHttpService.getCandidatesExplain({ talentIDs, positionId, isSamples }),
                staleTime: FIVE_MINUTES_MS,
                gcTime: FIVE_MINUTES_MS,
            });

            const setter = isSamples ? setSampleExplainsMap : setExplainsMap;
            setter((state) => (talentIDs || []).reduce((res, talentId) => {
                const explain = find(explains, { id: talentId });
                return ({ ...res, [talentId]: explain || {} });
            }, state));

            setExplainFetchingState((state) => (talentIDs || []).reduce((currentState, id) => ({ ...currentState, [id]: false }), state));
        } catch (error) {
            Logger.error(error);
        }
    }, [position?._id, queryClient]);

    const flushExplains = useCallback(() => {
        setExplainsMap({});
    }, []);

    const flushLocations = useCallback(() => {
        setLocationsMap({});
    }, []);

    const fetchExplain = useCallback(wrapFunction(fetchExplains, 500), [fetchExplains]);

    const getExplain = useCallback((talentId, isSample) => {
        if (!position || !talentId) {
            return null;
        }

        const result = isSample ? sampleExplainsMap[talentId] : explainsMap[talentId];

        if ((isNil(result)) && !explainFetchingState[talentId]) {
            fetchExplain(talentId, isSample);

            return null;
        }

        return result;
    }, [position, explainsMap, explainFetchingState, fetchExplain]);

    const isExplainFetching = useCallback((talentId) => !!explainFetchingState[talentId], [explainFetchingState]);

    const contextValue = useMemo(() => ({
        getExplain,
        fetchExplains,
        explainsMap,
        isExplainFetching,
        flushExplains,
        locationsMap,
        flushLocations,
        areLocationsFetched,
    }), [
        getExplain,
        fetchExplains,
        explainsMap,
        isExplainFetching,
        flushExplains,
        locationsMap,
        flushLocations,
        areLocationsFetched,
    ]);

    return (
        <ExplainContext.Provider
            value={contextValue}
        >
            {children}
        </ExplainContext.Provider>
    );
};

AppWithExplains.propTypes = {
    children: PropTypes.node,
};

export default ExplainContext;
